import React, { useState, useEffect } from "react";
import GenericPopup from "@Components/Popup/Popup";
import NoteAddRoundedIcon from "@mui/icons-material/NoteAddRounded";
import { Box } from "@mui/system";
import Joi from "joi";
import { OptionalDropDownValidation } from "@Utils/Validations/UserValidations/UserValidationFieldsSchema";
import DropDown from "../../Components/DropDown/DropDown";
import { validateForm } from "@Utils/Validations/HelperFunctions/FormValidationFunctions";
import { Grid, Typography, TextField, InputAdornment } from "@mui/material";
import DateSelector from "@Components/DateTimePickers/DatePicker";
import UploadFileButton from "../../Components/Buttons/uploadFileButton";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import LockIcon from "@mui/icons-material/Lock";
import useGetItem from "@Hooks/useGetItem";
import LoadingComponent from "@Components/LoadingComponent/LoadingComponent";
import { LIST_DOCUMENT_TYPES_CREATED } from "@API/APIRoutes";
import { fi } from "date-fns/locale";

export const InsertDocumentPopup = ({
  showPopup,
  closePopup,
  Confirm,
  loading,
  documentData,
  isEditing = false,
  selectedId,
}) => {
  const emptyDocument = {
    documentName: "Select Document Name",
    softCopyReceivalDate: null,
    hardCopyReceivalDate: null,
    comment: "",
    correct: false,
    softCopy: false,
    hardCopy: false,
  };

  const [errors, setErrors] = useState({
    documentName: "",
    softCopyReceivalDate: "",
    hardCopyReceivalDate: "",
    copyType: "",
  });
  const documentNames = [
    { id: "Select Document Name", name: "Select Document Name" },
    { id: "BOL", name: "BOL" },
    { id: "INVOICE", name: "Invoice" },
    { id: "PACKING_LIST", name: "Packing List" },
    { id: "COO", name: "COO" },
    { id: "COH", name: "COH" },
    { id: "FORM_4", name: "Form 4" },
    { id: "COA", name: "COA" },
    { id: "EURO1", name: "EURO1" },
    { id: "HEALTH_RELEASE_CERTIFICATE", name: "Health Release Certificate" },
    { id: "PHYTOSANITARY_CERTIFICATE", name: "Phytosanitary Certificate" },
    { id: "SET_OF_DOCUMENTS", name: "Set of Documents" },
  ];

  const [document, setDocument] = useState(
    documentData
      ? {
          documentName: documentData.documentName || "Select Document Name",
          softCopyReceivalDate: documentData.softCopyReceivalDate || null,
          hardCopyReceivalDate: documentData.hardCopyReceivalDate || null,
          comment: documentData.comment || "",
          correct: documentData.correct || false,
          softCopy: documentData.softCopy || false,
          hardCopy: documentData.hardCopy || false,
        }
      : { ...emptyDocument }
  );
  const [file, setFile] = useState();
  const [FileError, setFileError] = useState("");
  const [fileName, setFileName] = useState("");
  const [isCorrectChecked, setIsCorrectChecked] = useState(false);

  const getItem = useGetItem();
  const [isPopupLoading, setIsPopupLoading] = useState(false);
  const [SoftCopyChecked, setSoftCopyChecked] = useState(false);
  const [HardCopyChecked, setHardCopyChecked] = useState(false);
  const [isFormModified, setIsFormModified] = useState(false);
  const [AvailableDocumentTypes, setAvailableDocumentTypes] =
    useState(documentNames);

  const [originalDocument, setOriginalDocument] = useState(
    documentData ? { ...documentData } : null
  );
  // Replace both existing useEffects with this single useEffect
  useEffect(() => {
    // Handle document data changes (initialization or updates from parent)
    if (documentData) {
      // Format the incoming document data
      const formattedDoc = {
        documentName: documentData.documentName || "Select Document Name",
        softCopyReceivalDate: documentData.softCopyReceivalDate || null,
        hardCopyReceivalDate: documentData.hardCopyReceivalDate || null,
        comment: documentData.comment || "",
        correct: documentData.correct || false,
        softCopy: documentData.softCopy || false,
        hardCopy: documentData.hardCopy || false,
      };

      // Update state when documentData changes
      if (
        !originalDocument ||
        JSON.stringify(originalDocument) !== JSON.stringify(documentData)
      ) {
        setDocument({ ...formattedDoc });
        setOriginalDocument({ ...documentData });
        setIsCorrectChecked(documentData.correct || false);
        setSoftCopyChecked(documentData.softCopy || false);
        setHardCopyChecked(documentData.hardCopy || false);

        if (documentData.fileName) {
          setFileName(documentData.fileName);
        }

        // Initially not modified when documentData changes
        setIsFormModified(false);
      }
      // When document data exists but form values change, check for modifications
      else if (originalDocument) {
        // Compare current document with original
        const isChanged =
          document.documentName !== originalDocument.documentName ||
          document.softCopyReceivalDate?.valueOf() !==
            originalDocument.softCopyReceivalDate?.valueOf() ||
          document.hardCopyReceivalDate?.valueOf() !==
            originalDocument.hardCopyReceivalDate?.valueOf() ||
          document.comment !== originalDocument.comment ||
          document.correct !== originalDocument.correct ||
          document.softCopy !== originalDocument.softCopy ||
          document.hardCopy !== originalDocument.hardCopy ||
          (isEditing && file); // Consider file change in edit mode

        setIsFormModified(isChanged);
      }
    }
    // Handle case when documentData is null/undefined (new document)
    else {
      if (JSON.stringify(document) !== JSON.stringify(emptyDocument)) {
        // Check if the form has been modified from empty state
        const isChanged =
          document.documentName !== "Select Document Name" ||
          document.softCopyReceivalDate !== null ||
          document.hardCopyReceivalDate !== null ||
          document.comment !== "" ||
          document.correct !== false ||
          document.softCopy !== false ||
          document.hardCopy !== false ||
          file !== null;

        setIsFormModified(isChanged);
      } else {
        setOriginalDocument(null);
        setIsFormModified(false);
      }
    }
  }, [document, documentData, file, isCorrectChecked, isEditing]);

  useEffect(() => {
    setIsPopupLoading(true);

    if (showPopup && selectedId) {
      getItem(LIST_DOCUMENT_TYPES_CREATED, selectedId)
        .then((response) => {
          const unavailableNames = response.map((doc) =>
            doc.documentName?.toUpperCase()
          );

          const availableDocuments = documentNames.map((doc, index) => {
            const isUnavailable = unavailableNames?.includes(doc.id);

            return {
              ...doc,
              disabled: isUnavailable,
            };
          });
          setAvailableDocumentTypes(availableDocuments);
          setIsPopupLoading(false);
        })
        .catch((error) => {
          setIsPopupLoading(false);
          closePopup();
        });
    }
  }, [showPopup, selectedId]);

  const validationSchema = Joi.object({
    documentName: OptionalDropDownValidation("Document Name"),
    softCopyReceivalDate: Joi.optional(),
    hardCopyReceivalDate: Joi.optional(),
    comment: Joi.string().allow(""),
    correct: Joi.boolean(),
    softCopy: Joi.boolean(),
    hardCopy: Joi.boolean(),
  });

  const onSoftDatePick = (newValue) => {
    setDocument({ ...document, softCopyReceivalDate: newValue });
    setErrors({ ...errors, softCopyReceivalDate: "" });
  };
  const onHardDatePick = (newValue) => {
    setDocument({ ...document, hardCopyReceivalDate: newValue });
    setErrors({ ...errors, hardCopyReceivalDate: "" });
  };
  const onCancel = () => {
    setDocument({ ...emptyDocument });
    setFile(null);
    setFileName("");
    setSoftCopyChecked(false);
    setHardCopyChecked(false);
    closePopup();
    setErrors({
      documentName: "",
      softCopyReceivalDate: "",
      hardCopyReceivalDate: "",
    });
  };
  const handleFileChange = (event) => {
    const uploadedFile = event.target.files[0];
    // if (!uploadedFile) {
    //   setFileError(!file ? "File Is Required." : "");
    //   return;
    // }
    setFile(uploadedFile);
    setFileName(uploadedFile.name);
    setFileError("");
  };

  const handleChangeIsCorrectCheckBox = (event) => {
    setIsCorrectChecked(event.target.checked);
    setDocument({ ...document, correct: event.target.checked });
  };
  const handleChangeSoftCopyCheckBox = (event) => {
    setSoftCopyChecked(event.target.checked);
    setDocument({ ...document, softCopy: event.target.checked });

    // Clear the copyType error if at least one copy type is selected
    if (event.target.checked || document.hardCopy) {
      setErrors({ ...errors, copyType: "" });
    }
  };

  const handleChangeHardCopyCheckBox = (event) => {
    setHardCopyChecked(event.target.checked);
    setDocument({ ...document, hardCopy: event.target.checked });

    // Clear the copyType error if at least one copy type is selected
    if (event.target.checked || document.softCopy) {
      setErrors({ ...errors, copyType: "" });
    }
  };
  const content = isPopupLoading ? (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      height="150px" // Adjust height as needed
    >
      <LoadingComponent />
    </Box>
  ) : (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Document Name
        </Typography>
        {!isEditing && (
          <DropDown
            state={document.documentName}
            setState={(value) => {
              setDocument({ ...document, documentName: value });
              setErrors({ ...errors, documentName: "" }); // Clear error message on selection
            }}
            name="document Name"
            label=""
            items={AvailableDocumentTypes}
            placeholder="Select Document Name"
            helperText={errors.documentName}
            disabled={isEditing}
          />
        )}
        {isEditing && (
          <TextField
            variant="outlined"
            fullWidth
            name="documentName"
            type="text"
            disabled
            value={document.documentName}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <LockIcon size="1.5em" thickness={5} color="gray" />
                </InputAdornment>
              ),
            }}
          />
        )}
      </Grid>
      <Grid item xs={12}>
        <Typography variant="body1" sx={{ mb: 1 }}>
          Comment (Optional)
        </Typography>
        <TextField
          variant="outlined"
          fullWidth
          name="comment"
          type="text"
          placeholder="Comment"
          value={document.comment}
          onChange={(e) =>
            setDocument({ ...document, comment: e.target.value })
          }
        />
      </Grid>
      {document.softCopy && (
        <Grid item xs={12}>
          <DateSelector
            name={"Soft Copy Receival Date"}
            value={document.softCopyReceivalDate}
            error={errors.softCopyReceivalDate}
            onPick={onSoftDatePick}
          />
        </Grid>
      )}
      {document.hardCopy && (
        <Grid item xs={12}>
          <DateSelector
            name={"Hard Copy Receival Date"}
            value={document.hardCopyReceivalDate}
            error={errors.hardCopyReceivalDate}
            onPick={onHardDatePick}
          />
        </Grid>
      )}
      <Grid item xs={12}>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
          }}
        >
          <FormControlLabel
            label="The document is correct"
            control={
              <Checkbox
                checked={isCorrectChecked}
                onChange={handleChangeIsCorrectCheckBox}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />

          <FormControlLabel
            label="Soft Copy"
            control={
              <Checkbox
                checked={SoftCopyChecked}
                onChange={handleChangeSoftCopyCheckBox}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />

          <FormControlLabel
            label="Hard Copy"
            control={
              <Checkbox
                checked={HardCopyChecked}
                onChange={handleChangeHardCopyCheckBox}
                inputProps={{ "aria-label": "controlled" }}
              />
            }
          />
        </Box>
        {errors.copyType && (
          <Typography variant="body2" color="error" sx={{ mt: 1 }}>
            {errors.copyType}
          </Typography>
        )}
      </Grid>
      <Grid item xs={12}>
        <UploadFileButton
          handleFileChange={handleFileChange}
          fileName={fileName}
          FileError={FileError}
        />
        {isEditing && !file && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="body1" sx={{ mb: 1 }}>
              There is a document already uploaded. If you want to change it,
              upload a new one.
            </Typography>
          </Box>
        )}
      </Grid>
    </Grid>
  );

  const submit = () => {
    const formattedDocument = {
      ...document,
      softCopyReceivalDate: document.softCopyReceivalDate
        ? typeof document.softCopyReceivalDate.toDate === "function"
          ? document.softCopyReceivalDate.toDate()
          : document.softCopyReceivalDate instanceof Date
          ? document.softCopyReceivalDate
          : new Date(document.softCopyReceivalDate)
        : null,
      hardCopyReceivalDate: document.hardCopyReceivalDate
        ? typeof document.hardCopyReceivalDate.toDate === "function"
          ? document.hardCopyReceivalDate.toDate()
          : document.hardCopyReceivalDate instanceof Date
          ? document.hardCopyReceivalDate
          : new Date(document.hardCopyReceivalDate)
        : null,
    };

    let hasValidationError = false;

    // Check if at least one copy type is selected
    if (!formattedDocument.softCopy && !formattedDocument.hardCopy) {
      setErrors({
        ...errors,
        copyType:
          "Please select at least one copy type (Soft Copy or Hard Copy)",
      });
      hasValidationError = true;
    }

    if (document.softCopy && document.softCopyReceivalDate == null) {
      setErrors({
        ...errors,
        softCopyReceivalDate: "Please choose Date for Soft Copy ReceivalDate",
      });
      hasValidationError = true;
    }

    if (document.hardCopy && document.hardCopyReceivalDate == null) {
      setErrors({
        ...errors,
        hardCopyReceivalDate: "Please Choose Date for Hard Copy Receival Date",
      });
      hasValidationError = true;
    }

    if (
      !hasValidationError &&
      validateForm(formattedDocument, setErrors, validationSchema)
    ) {
      if (formattedDocument.hardCopy == false) {
        formattedDocument.hardCopyReceivalDate = null;
      }
      if (formattedDocument.softCopy == false) {
        formattedDocument.softCopyReceivalDate = null;
      }
      Confirm(formattedDocument, file);
      setDocument({ ...emptyDocument });
      setFile(null);
      setFileName("");
      setErrors({
        documentName: "",
        softCopyReceivalDate: "",
        hardCopyReceivalDate: "",
        copyType: "",
      });
      setIsCorrectChecked(false);
      setSoftCopyChecked(false);
      setHardCopyChecked(false);
    }
  };

  return (
    <GenericPopup
      icon={<NoteAddRoundedIcon color="white" fontSize="large" />}
      open={showPopup}
      title={isEditing ? `Edit Document` : `Add Document`}
      content={content}
      buttons={
        isPopupLoading
          ? []
          : [
              {
                text: "Cancel",
                onClick: onCancel,
                color: "secondary",
                variant: "outlined",
              },
              {
                text: isEditing ? "Edit" : "Add",
                onClick: submit,
                color: "primary",
                variant: "contained",
                loading: loading,
                disabled: isEditing && !isFormModified,
                sx: {
                  "&.Mui-disabled": {
                    backgroundColor: "#e0e0e0",
                    color: "#9e9e9e",
                  },
                },
              },
            ]
      }
    />
  );
};
